import React, { useEffect } from 'react';
import { withRouter } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import HealtheraTable from '../Table/Table';
import { MainTitle } from '../../healtheraComponents';
import * as actions from '../../redux/organisations';
import useLocationQuery from '../../hooks/useQuery';
import LoadingCell from '../Services/components/Loading/LoadingCell';
import TableContainer from '../Shared/TableContainer';
import { formatDate } from '../../utils/formatters';
import { CONTRACT_START_DATE } from '../../constants';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import { ORGANISATION_PLANS, organisationPlanLabel } from '../Shared/organisationPlans';

function PharmaciesList(props) {
  const { history } = props;

  const dispatch = useDispatch();

  const { query, changeLocationQuery } = useLocationQuery();

  const { orgID } = useSelector(state => state.authentication);
  const {
    organisations,
    totalCount,
    fetching,
    type,
    searchResults,
    filter,
  } = useSelector(state => state.organisations);

  const setFilter = filter => {
    dispatch(actions.setFilter(filter));
  };

  useEffect(() => {
    // If an Account Manager logs in, redirect to their orgs details page
    if (orgID) history.push(`${process.env.PUBLIC_URL}/organisations/${orgID}`);

    dispatch(actions.showTabIcon(true));
    dispatch(actions.changeTab(0));
  }, [dispatch, orgID, history]);

  useEffect(() => {
    if (!query.search) {
      dispatch(
        actions.getOrganisations(
          null,
          query.page,
          query.type || 'active',
          filter,
        ),
      );
    }
    return ()=>{
      dispatch(actions.resetOrganisationsListAction())
      dispatch(actions.resetSearchResults())
    }
  }, [dispatch, filter, query, type, history]);

  const handleChangePage = (_, page) => {
    changeLocationQuery({
      ...query,
      page
    });
  };

  const preparedData = fetching
    ? prepareLoadingData(10, { filter, setFilter })
    : prepareData(organisations, { filter, setFilter });

  return (
    <TableContainer>
      {searchResults && <SearchResults data={searchResults} filter={{ filter, setFilter }} />}
      {!searchResults && (
        <HealtheraTable
          displayPagination
          page={+query.page || 0}
          rowsPerPage={10}
          totalCount={totalCount}
          data={preparedData}
          onChangePage={handleChangePage}
        />
      )}
    </TableContainer>
  );
}

function getPaymentState(org) {
  if (!org.payment) return <CloseIcon fontSize={'large'} nativeColor={'#2f9d6d'} />;
  if (org.payment.stripe_connect_id && org.payment.mobile_payment_activated)
    return <CheckIcon fontSize={'large'} nativeColor={'#2f9d6d'} />;
  if (org.payment.stripe_connect_id) return <PaymentField>Stripe Account Submitted</PaymentField>;
  if (org.payment.date_invited) return <PaymentField>Invited</PaymentField>;
  return <CloseIcon fontSize={'large'} nativeColor={'#2f9d6d'} />;
}

export default withRouter(PharmaciesList);

const SearchResults = ({ data, filter }) => {
  const pharmacyResults = data.pharmacies;
  const { plan } = filter.filter;
  const orgResults = plan ? (data.orgs || []).filter(org => org.plan === plan) : data.orgs;

  const preparePharmaciesData = (branches = []) => {
    return branches.reduce(
      (data, branch) => {
        const {
          _id,
          pharmacy_name,
          address: { postcode },
          status,
          postcode: legacyPostcode,
          NACSCode,
        } = branch;

        data.push({
          view: {
            name: pharmacy_name || '',
            nacsCode: NACSCode || '',
            postcode: postcode || legacyPostcode || '',
            status,
          },
          ID: _id,
          route: 'branches'
        });

        return data;
      },
      [
        {
          view: {
            name: 'Pharmacy Name',
            nacsCode: 'ODS Code',
            postcode: 'Post Code',
            status: 'Status',
          },
          headers: true
        }
      ]
    );
  };

  return (
    <div>
      <HealtheraTable muted={true} data={prepareData(orgResults, filter)} />
      <HealtheraTable muted={true} data={preparePharmaciesData(pharmacyResults)} />
    </div>
  );
};

const getTableHeaders = filters => ({
  view: {
    name: 'Organisation Name',
    plan: filters ? (
      <MainTitle>
        <Select
          value={filters.filter.plan || 0}
          onChange={e => filters.setFilter({ plan: e.target.value === 0 ? undefined : e.target.value })}
          disableUnderline
        >
          <MenuItem value={0}>
            <DropdownFilter>Plan</DropdownFilter>
          </MenuItem>
          {ORGANISATION_PLANS.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              <DropdownFilter>{label}</DropdownFilter>
            </MenuItem>
          ))}
        </Select>
      </MainTitle>
    ) : (
      <DropdownFilter>Plan</DropdownFilter>
    ),
    startDate: <TableSortLabel
      IconComponent={ArrowDropUpIcon}
      active={true}
      direction={filters.filter.startDate}
      onClick={() => {
        const newsSortContractStart =
          filters.filter.startDate === CONTRACT_START_DATE.ASC
            ? CONTRACT_START_DATE.DESC
            : CONTRACT_START_DATE.ASC;
        filters.setFilter({ startDate: newsSortContractStart });
      }}
    >
      Start Date
  </TableSortLabel>,
    contract: 'Contract Length',
    commissionable: filters ? (
      <MainTitle>
        <Select
          value={
            typeof filters.filter.commissionable !== 'boolean' ? 0 : filters.filter.commissionable
          }
          onChange={e =>
            filters.setFilter({ commissionable: e.target.value === 0 ? undefined : e.target.value })
          }
          disableUnderline
        >
          <MenuItem value={0}>
            <DropdownFilter>Commission Status</DropdownFilter>
          </MenuItem>
          <MenuItem value={true}>
            <DropdownFilter green>Commissioned</DropdownFilter>
          </MenuItem>
          <MenuItem value={false}>
            <DropdownFilter red>Not Commissioned</DropdownFilter>
          </MenuItem>
        </Select>
      </MainTitle>
    ) : (
      <DropdownFilter>Commission Status</DropdownFilter>
    ),
    payment: filters ? (
      <MainTitle>
        <Select
          value={filters.filter.payment || 0}
          onChange={e =>
            filters.setFilter({ payment: e.target.value === 0 ? undefined : e.target.value })
          }
          disableUnderline
        >
          <MenuItem value={0}>
            <DropdownFilter>Payment State</DropdownFilter>
          </MenuItem>
          <MenuItem value={'active'}>
            <DropdownFilter green>Active</DropdownFilter>
          </MenuItem>
          <MenuItem value={'invited'}>
            <DropdownFilter>Invited</DropdownFilter>
          </MenuItem>
          <MenuItem value={'deactivated'}>
            <DropdownFilter red>Deactivated</DropdownFilter>
          </MenuItem>
        </Select>
      </MainTitle>
    ) : (
      <DropdownFilter>Payment State</DropdownFilter>
    )
  },
  headers: true
});

const prepareLoadingData = (rowsPerPage, filters) => {
  return Array.from({ length: rowsPerPage }).reduce(
    data => {
      data.push({
        view: {
          name: <LoadingCell height={48} />,
          plan: <LoadingCell />,
          startDate: <LoadingCell />,
          contract: <LoadingCell />,
          commissionable: <LoadingCell />,
          payment: <LoadingCell />
        }
      });
      return data;
    },
    [getTableHeaders(filters)]
  );
};

const prepareData = (orgs = [], filters) => {
  return orgs.reduce(
    (data, org = {}) => {
      let trialEndDate;
      const {
        active,
        currentContract: {
          trialPeriod,
          startDate: contractStartDate,
          count: contractLength
        } = {},
        saas_billing: {
          contract_term: { contract_start: saasContractStart } = {}
        } = {},
        name,
        _id,
        commissions,
        plan
      } = org;

      if (contractStartDate) {
        // Calculate the trial end date
        const startDate = new Date(contractStartDate);
        const daysInStartMonth = new Date(
          startDate.getFullYear(),
          startDate.getMonth() + 1,
          0
        ).getDate();
        const trialPeriodInDays = daysInStartMonth * trialPeriod;
        trialEndDate = new Date(startDate.setDate(startDate.getDate() + trialPeriodInDays));
      }

      const ComIndicator = !!commissions ? CheckIcon : CloseIcon;

      data.push({
        view: {
          name: name || '',
          plan: organisationPlanLabel(plan),
          startDate: saasContractStart ? formatDate(saasContractStart * 1000): '',
          contract: contractLength ? `${contractLength} Months` : '',
          commissionable: <ComIndicator fontSize={'large'} nativeColor={'#2f9d6d'} />,
          payment: getPaymentState(org)
        },
        flags: {
          trial: new Date() < trialEndDate && active ? 1 : 0,
          agreement: 0
        },
        ID: _id,
        route: 'organisations'
      });
      return data;
    },
    [getTableHeaders(filters)]
  );
};

const DropdownFilter = styled.span`
  color: ${props => (props.red ? '#c82b2b' : props.green ? '#2f9d6d' : 'rgba(0, 0, 0, 0.54)')};
  font-size: 0.75rem;
  font-weight: 500;
`;

const PaymentField = styled.span`
  color: #78a64f;
  font-weight: 500;
`;
